import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CiLogin } from 'react-icons/ci';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { useNavigate, Link } from 'react-router-dom';
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import { Toast } from 'primereact/toast';
import { Collections } from '../types/collection';
import api from '../libs/axiosInstance';
import { enhancedPlans } from '../features/payments/enhanced-pricing-plans.type';
import { captureException } from '@sentry/react';
import { analytics } from '../features/firebase/firebase-config';
import { useUserFriendlyError } from '../components/UserFriendlyErrorToast';
import SEO from '../features/seo/seo.feature';
import { FaGoogle, FaLock, FaEnvelope, FaArrowRight } from 'react-icons/fa';

interface FormInputs {
  email: string;
  password: string;
}

const schema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email Address is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const Login: React.FC = () => {
  analytics.logEvent('page_view', {
    page_title: 'login',
  });

  // Define structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Login | PrepBestie",
    "description": "Sign in to your PrepBestie account to continue your interview and public speaking practice with AI-powered feedback",
    "publisher": {
      "@type": "Organization",
      "name": "PrepBestie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prepbestie.com/prepbestie.png"
      }
    },
    "mainContentOfPage": {
      "@type": "WebPageElement",
      "cssSelector": ".login-container"
    }
  };

  const toast = useRef<Toast>(null);
  const [loading, toggleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { showError } = useUserFriendlyError();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormInputs) => {
    try {
      toggleLoading(true);
      const { email, password } = data;
      await firebase.auth().signInWithEmailAndPassword(email, password);
      toggleLoading(false);
      navigate('/dashboard');
    } catch (error: firebase.auth.AuthError | unknown) {
      toggleLoading(false);
      handleLoginError(error as firebase.auth.AuthError);
    }
  };

  const handleLoginError = (error: firebase.auth.AuthError) => {
    captureException(error)
    let errorMessage = 'Unable to login. Please try again or contact support.';
    if(error.message.includes('INVALID_LOGIN_CREDENTIALS')) {
      errorMessage = 'Incorrect email or password. Please try again.';
      const authError = new Error(errorMessage);
      (authError as any).code = 'auth/login-failed';
      showError(authError, {
        action: 'login'
      }, toast);
      return;
    }
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No user found with this email.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'The email address is not valid.';
        break;
      default:
        errorMessage = 'Unable to login. Please try again.';
        break;
    }
    // Use user-friendly error handling for authentication errors
    const authError = new Error(errorMessage);
    (authError as any).code = 'auth/login-failed';
    showError(authError, {
      action: 'login'
    }, toast);
  };

  // Google sign-in logic
  const googleProvider = new firebase.auth.GoogleAuthProvider();
  const handleGoogleSignIn = async () => {
    try {
      toggleLoading(true);
      const result = await firebase.auth().signInWithPopup(googleProvider);
      const user = result.user;
      if (!user) {
        toggleLoading(false);
        toast.current?.show({
          severity: "error",
          detail: 'No user information returned from Google.',
        });
        return;
      }
      const userId = user.uid;
      const docRef = firebase.firestore().collection(Collections.Users).doc(userId);
      const docSnapshot = await docRef.get();
      if (!docSnapshot.exists) {
        const fullName = user.displayName;
        const email = user.email;
        await docRef.set({
          fullName,
          email,
          createdAt: new Date().toDateString(),
          lastPractice: [],
        }, { merge: true });
        const response = await api.post('/payment/user/create', {
          email,
          name: fullName,
          userId,
        });
        await api.get(`/account/welcome/${userId}`);
        if ([200, 201].includes(response.status)) {
          await docRef.update({
            subscription: enhancedPlans[0],
            subscriptionId: null,
          });
        }
      }
      toggleLoading(false);
      navigate('/dashboard');
    } catch (error: firebase.auth.AuthError | unknown) {
      toggleLoading(false);
      captureException(error)
      // Use user-friendly error handling for Google auth errors
      showError(error, {
        action: 'google-signin'
      }, toast);
    }
  };

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.1
      }
    }
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.03, transition: { duration: 0.3 } },
    tap: { scale: 0.97, transition: { duration: 0.15 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-700 via-indigo-800 to-purple-900 flex items-center justify-center p-4 sm:p-6">
      <SEO
        title='Login | PrepBestie'
        description='Sign in to your PrepBestie account to access AI-powered interview practice, speech analysis, and personalized feedback to improve your communication skills.'
        name='PrepBestie'
        type='website'
        structuredData={structuredData}
        keywords={[
          'Prep Bestie',
          'PrepBestie',
          'PrepBestie login',
          'PrepBestie account',
          'Interviews',
          'Interview preparation',
          'CV builder',
          'vacancies',
          'Pitch preparation',
          'Public speech',
          'AI',
          'AI tools',
          'VC',
          'Account access',
          'User login',
          'Speech analysis',
          'Communication skills'
        ]}
      />
      <Toast ref={toast} />

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-0 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-md relative z-10">

        <motion.div
          className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden login-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative pb-1">
            {/* Login Header with Gradient */}
            <div className="relative p-8 pb-6">
              <motion.div
                className="flex flex-col items-center justify-center"
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.div
                  className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-tr from-indigo-500 to-indigo-700 mb-4 shadow-lg"
                  variants={fadeIn}
                >
                  <CiLogin className="text-white text-3xl" />
                </motion.div>
                <motion.h2
                  className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-100 mb-2"
                  variants={fadeIn}
                >
                  Welcome Back
                </motion.h2>
                <motion.p
                  className="text-white/70 text-sm text-center max-w-xs"
                  variants={fadeIn}
                >
                  Sign in to continue your journey to better communication
                </motion.p>
              </motion.div>
            </div>

            {/* Login Form */}
            <div className="bg-white rounded-tl-3xl rounded-tr-3xl p-6 sm:p-8">
              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                {/* Email */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <label className="block text-gray-700 text-sm font-medium mb-1">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      {...register('email')}
                      type="email"
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none ${errors.email
                          ? 'border-red-300 focus:border-red-500 focus:ring focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200'
                        } transition-all duration-200`}
                      placeholder="you@example.com"
                    />
                  </div>
                  <AnimatePresence>
                    {errors.email && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.email.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Password */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <label className="block text-gray-700 text-sm font-medium mb-1">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400" />
                    </div>
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:outline-none ${errors.password
                          ? 'border-red-300 focus:border-red-500 focus:ring focus:ring-red-200'
                          : 'border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200'
                        } transition-all duration-200`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? <AiFillEyeInvisible className="text-lg" /> : <AiFillEye className="text-lg" />}
                    </button>
                  </div>
                  <AnimatePresence>
                    {errors.password && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.password.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Forgot Password Link */}
                <motion.div
                  className="flex justify-end"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <Link
                    to="/forgot-password"
                    className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <motion.button
                    type="submit"
                    className={`w-full py-3 px-6 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-800 hover:from-indigo-700 hover:to-indigo-900 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${loading ? 'opacity-80' : ''}`}
                    disabled={loading}
                    variants={buttonVariants}
                    initial="idle"
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      <>
                        <span>Sign In</span>
                        <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>

              {/* Divider */}
              <motion.div
                className="flex items-center my-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <div className="flex-1 h-px bg-gray-300"></div>
                <div className="px-4 text-sm text-gray-500">or continue with</div>
                <div className="flex-1 h-px bg-gray-300"></div>
              </motion.div>

              {/* Social Login */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              >
                <motion.button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-xl py-3 px-6 text-gray-700 hover:bg-gray-50 hover:shadow-md transition-all duration-300"
                  variants={buttonVariants}
                  initial="idle"
                  whileHover="hover"
                  whileTap="tap"
                >
                  <FaGoogle className="text-red-500" />
                  <span>Sign in with Google</span>
                </motion.button>
              </motion.div>

              {/* Sign Up Link */}
              <motion.p
                className="text-center text-gray-600 mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                Don't have an account?{' '}
                <Link to="/create-account" className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors font-medium">
                  Create one now
                </Link>
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-xs text-white/50">
            &copy; {new Date().getFullYear()} PrepBestie. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
